/**
 * Module #28 chunk 4 — native camera bridge.
 *
 * `capturePhotoNative()` returns a `File` from the Capacitor `Camera`
 * plugin when running on a native shell, or `null` outside Capacitor
 * (callers fall back to the standard `<input type="file" capture>` web
 * flow). The function is dynamic-import-only so the web bundle stays
 * small and a browser tab without Capacitor doesn't fail at runtime.
 *
 * Why this exists: iOS WKWebView's `<input capture>` works but has known
 * stability issues for sustained capture sessions (the cataloging worker
 * flow shoots 50–200 photos in a row). The native plugin uses
 * AVFoundation directly + handles permissions / orientation / preview
 * correctly. Android Chrome WebView is mostly fine but native is still a
 * tighter loop (no "Done" dialog round-trip).
 *
 * The returned File slots into the existing AdminCatalog `addPhotoToLot`
 * flow without any other changes — same FormData append, same compress
 * pipeline.
 */

// deno-lint-ignore-file no-explicit-any

interface CapacitorCoreShape {
  isNativePlatform: () => boolean;
}

interface CameraPluginShape {
  getPhoto: (opts: {
    quality?: number;
    allowEditing?: boolean;
    resultType: 'base64' | 'uri' | 'dataUrl';
    source?: 'CAMERA' | 'PHOTOS' | 'PROMPT';
    saveToGallery?: boolean;
    correctOrientation?: boolean;
    width?: number;
    height?: number;
  }) => Promise<{
    dataUrl?: string;
    base64String?: string;
    webPath?: string;
    format: string;
  }>;
}

async function loadCameraModule(): Promise<{
  Capacitor: CapacitorCoreShape;
  Camera: CameraPluginShape;
} | null> {
  try {
    const core = (await import('@capacitor/core')) as { Capacitor?: CapacitorCoreShape };
    const cam = (await import('@capacitor/camera')) as { Camera?: CameraPluginShape };
    if (!core.Capacitor || !cam.Camera) return null;
    return { Capacitor: core.Capacitor, Camera: cam.Camera };
  } catch {
    return null;
  }
}

function dataUrlToFile(dataUrl: string, filename: string): File | null {
  const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) return null;
  const [, mime, b64] = match;
  if (!mime || !b64) return null;
  try {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], filename, { type: mime });
  } catch {
    return null;
  }
}

export interface CapturePhotoNativeOptions {
  /** 'CAMERA' jumps straight to capture; 'PROMPT' shows camera-or-library. */
  source?: 'CAMERA' | 'PROMPT';
  /** Filename base used for the returned File. Default 'lot-photo'. */
  filenameBase?: string;
}

/**
 * Return a File from the Capacitor Camera plugin, or `null` if running
 * outside Capacitor (no native shim). Caller treats null as "fall back
 * to the web file-picker."
 *
 * Throws only on a NATIVE error (permission denied after grant, user
 * cancels). Callers should display the error message + offer a retry.
 */
export async function capturePhotoNative(
  options: CapturePhotoNativeOptions = {},
): Promise<File | null> {
  const mods = await loadCameraModule();
  if (!mods) return null;
  if (!mods.Capacitor.isNativePlatform()) return null;

  const { Camera } = mods;
  const photo = await Camera.getPhoto({
    quality: 88,
    allowEditing: false,
    resultType: 'dataUrl',
    source: options.source ?? 'CAMERA',
    saveToGallery: false,
    correctOrientation: true,
  });

  if (!photo.dataUrl) return null;
  const extension = photo.format === 'png' ? 'png' : 'jpg';
  const filename = `${options.filenameBase ?? 'lot-photo'}-${Date.now()}.${extension}`;
  return dataUrlToFile(photo.dataUrl, filename);
}

/**
 * Synchronous best-effort detection of whether the native camera bridge
 * is available, useful for conditionally rendering a "Take photo (native)"
 * button alongside the standard file input on web. Reads the
 * `globalThis.Capacitor` shim that's set at native-shell init.
 */
export function isNativeCameraAvailable(): boolean {
  if (typeof globalThis === 'undefined') return false;
  const Capacitor = (globalThis as { Capacitor?: CapacitorCoreShape }).Capacitor;
  if (!Capacitor || typeof Capacitor.isNativePlatform !== 'function') return false;
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}
